<template>
  <form @submit.prevent="checkIfFromGame">
    <v-container class="pa-0 pt-3 mt-3">
      <v-row justify="space-between" align="center">
        <v-col cols="12" md="9">
          <smart-input
              dense
              rounded
              outlined
              v-model="form.question"
              textarea
              :error="inputErrors.question"
              :required="true"
              :label="$t('pages.createGame.questionContent')"
              rows="3"
              hide-details
          />
        </v-col>
        <v-col cols="12" md="3">
          <add-image :image="form.image" @selectedImage="(img) => {form.image = img}" :image-dimension="imageDimension.inside"></add-image>

        </v-col>
      </v-row>
      <div v-if ="showDifficultyAndCategory" class="jeopardy-panel px-5 py-3 mb-3 mt-5">
        <div class="jeopardy-panel-title">
          <span>{{ $t('gameTypes.jeopardy') }}</span>
          <span><v-icon>mdi-crown</v-icon></span>
        </div>
      <v-row align="center" class="px-3 pt-1">
        <label>{{ $t('pages.createQuestion.questionDifficulty') }}</label>
        <v-rating v-model="form.difficulty"></v-rating>
      </v-row>
      <v-row class="px-1 pb-3" align="center">
          <label for="category-name" class="px-2">{{ $t('pages.createQuestion.category') }}</label>
          <v-combobox
              style="width: 100%; max-width: 225px;"
              id="category-name"
              v-model="form.category_name"
              :items="categories"
              hide-selected
              small-chips
              hide-details
              dense
          >
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    {{$t('pages.createQuestion.press')}}
                    <kbd>enter</kbd>
                    {{$t('pages.createQuestion.toAddNewCategory')}}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-combobox>
      </v-row>
      </div>
      <v-row>
        <c-card color="main_pink" class="my-2 slim-card" v-if="!isMusagQuestion" style="width: 100%">
          <h4 slot="title">{{ $t('pages.createQuestion.answers') }}</h4>
          <div class="my-1">
            <answer-form
                :can-remove="false"
                :label="$t('pages.createQuestion.theCorrectAnswer')"
                :answer="answer"
                :media="false"
                @updatedAnswerImage="updateAnswerImage"
            />
          </div>
          <div>
            <answer-form
                v-for="a in worngAnswers"
                :key="a.id"
                :label="$t('pages.createQuestion.wrongAnswer')"
                :answer="a"
                @remove="removeAnswer(a)"
                @updatedAnswerImage="updateAnswerImage"
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    small
                    class="mx-3"
                    outlined
                    @click="addAnswer()"
                    v-bind="attrs"
                    v-on="on"
                    fab
                >
                  <v-icon circle dark> add</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('pages.createQuestion.addAnswer') }}</span>
            </v-tooltip>
          </div>
        </c-card>
      </v-row>
      <div class="mt-3">
        <v-row>
          <v-col>
            <v-expansion-panels>
              <v-expansion-panel class="my-2">
                <v-expansion-panel-header>
                  {{ $t('pages.createQuestion.displayContentBeforeQuestion') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row justify="space-around">
                    <v-col align="center">
                      <add-media v-model="form.before_question"
                                 :media_type="form.before_question_media"
                                 :additional_text="form.before_question_text"
                                 :label="$t('pages.createQuestion.mediaBeforeQuestion')"
                                 @additional_text="form.before_question_text = $event"
                                 @on_media_type="form.before_question_media = $event"
                                 :image-dimension="imageDimension.background"
                      />
                    </v-col>
                  </v-row>

                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel class="my-2">
                <v-expansion-panel-header>
                  {{ $t('pages.createQuestion.displayContentAfterQuestion') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row justify="space-around">
                    <v-col align="center">
                      <add-media
                          v-model="form.after_question"
                          :media_type="form.after_question_media"
                          :additional_text="form.after_question_text"
                          :label="$t('pages.createQuestion.mediaAfterQuestion')"
                          @additional_text="form.after_question_text = $event"
                          @on_media_type="form.after_question_media = $event"
                          :image-dimension="imageDimension.background"
                      />
                    </v-col>
                  </v-row>

                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel class="mt-2">
                <v-expansion-panel-header>
                  {{ $t('pages.createQuestion.advanced') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <smart-input
                      v-model="form.hint"
                      :label="$t('pages.createQuestion.hint')"
                      :error="inputErrors.hint"
                  />
                  <tags-select :initial-tags="form.tags" @input="form.tags = $event" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </div>
      <cols-row cols="6" :row="{ justify: 'center' }">
        <btn-loader rounded x-large :progress="false"> {{ $t('generics.save') }}</btn-loader>
        <v-btn rounded outlined x-large type="button" class="my-2" @click="onCancel">
          {{ $t('generics.cancel') }}
        </v-btn>
      </cols-row>
    </v-container>
  </form>
</template>

<script>
import QuestionModule from "../../store/Question.module";
import FormView from "src/mixins/FormView";
import TagsSelect from "../questions/TagsSelect";
import QuestionTypeSelect from "../questions/QuestionTypeSelect";
import AddMedia from "../questions/AddMedia";
import AnswerForm from "../answers/AnswerForm";
import { uniqueId } from "lodash";
import CCard from "dev/scaffoldings/cCard";
import BtnLoader from "dev/components/BtnLoader";
import MediaGallery from "../../../../../../src/packages/gallery/MediaGallery";
import AddImage from "root/packages/closeapp/QuestionsManager/assets/views/questions/AddImage";
import { types as TypeCategory } from "src/store/modules/Category.module";
import GameQuestionService from "src/services/GameQuestionService";
import imageDimensionType from "src/types/ImageDimensionType";
import { isRtl } from "src/locales";
export default {
  name: "QuestionForm",
  components: {
    BtnLoader,
    CCard,
    AnswerForm,
    QuestionTypeSelect,
    TagsSelect,
    AddMedia,
    MediaGallery,
    AddImage
  },
  props: {
    fromGameMenu: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      form: {
        name: null,
        before_question_media: "",
        before_question_text: "",
        after_question_media: "",
        after_question_text: ""
      },
      answer: {},
      answers: [],
      belongsToAdditionalGames: false,
      imageDimension: imageDimensionType
    };
  },
  mounted() {
    if (this.form.id)
      this.checkQuestionBelongToAdditionalGames();
    this.$store.dispatch(TypeCategory.FETCH, this.$route.params.id);
  },
  mixins: [new FormView(QuestionModule, "question")],
  computed: {
    isNew() {
      return !!this.question.id;
    },
    worngAnswers() {
      return this.answers.filter(a => a.id != this.answer.id);
    },
    hasAnswerContent() {
      return this.answers.filter(a => !!a.content).length;
    },
    game() {
      return this.$store.state.GameModule.game;
    },
    isMusagQuestion() {
      return this.form.question_type === 5;
    },
    categories() {
      return this.$store.state.CategoryModule.categories.map(category => category.name)
    },
    showDifficultyAndCategory() {
      return !!this.game.type.category_and_difficulty;
    }
  },
  watch: {
    "form.answers": {
      handler() {
        if (this.form.answers && this.form.answers.length) {
          this.answers = this.form.answers.slice(0);
        }
      },
      immediate: true
    },
    "form.answer": {
      immediate: true,
      handler() {
        this.answer = Object.assign({}, this.form.answer);
      }
    }
  },
  methods: {
    async checkIfFromGame(e) {
      if (this.belongsToAdditionalGames && this.fromGameMenu && this.form.id) {
        const response = await this.$notification.question(
            this.$t('pages.createQuestion.questionLinkedToMultipleGames'),
            this.$t('pages.createQuestion.EditThisQuestionForAllGames'),
            {
              icon: "warning",
              confirmButtonColor: this.$vuetify.theme.themes.light.main_pink,
              denyButtonColor: "gray",
              confirmButtonText: this.$t('pages.createQuestion.forAllGames'),
              showCancelButton: false,
              showDenyButton: true,
              reverseButtons: !!isRtl(),
              denyButtonText: this.$t('pages.createQuestion.forThisGameOnly')
            }
        )
        if (response.isDenied) {
          this.form.thisGameOnly = true;
          this.onSubmit(e);
        } else if (response.isConfirmed) {
          this.form.thisGameOnly = false;
          this.onSubmit(e);
        }

      } else {
        this.form.thisGameOnly = false;
        this.onSubmit(e);
      }
    },
    getData(e) {
      this.form.answer = this.answer;
      if (
          this.form.before_question_media === "img" &&
          this.form.before_question === null
      ) {
        this.form.before_question_media = "text";
      }
      const data = Object.assign({}, this.form, { answers: this.answers });
      data.newTags = data.tags.filter(tag => tag && typeof tag === "string");
      data.tags = data.tags.filter(tag => tag.id).map(tag => tag.id);
      return data;
    },

    removeAnswer(answer) {
      const index = this.answers.indexOf(answer);
      if (index > -1) {
        this.answers.splice(index, 1);
      }
    },
    addAnswer(settings = []) {
      this.answers.push({
        id: uniqueId("notreal")
      });
    },
    onCancel() {
      this.$emit("cancel", this.gamelog);
    },
    updateAnswerImage(img) {
      this.answer.media_src = img.imageSrc;
    },
    checkQuestionBelongToAdditionalGames() {
      GameQuestionService.belongToAdditionalGames(this.form.id, this.game.id)
          .then((response) => {
            this.belongsToAdditionalGames = response?.data?.result;
          })
    }
  }
};
</script>

<style lang="scss">
.slim-card header {
  height: 38px !important;

  & > div {
    height: 38px !important;
  }

  h3, h4 {
    padding: 0 5px;
  }
}
</style>

<style scoped lang="scss">
.close {
  cursor: pointer;
}

.around-image {
  border-radius: 100px;
}

.image-upload-label {
  width: 75px;
  height: 75px;
  border: 1px black solid;
}
.jeopardy-panel {
  border: 1px solid  rgba(0, 0, 0, 0.38);
  border-radius: 30px;
  position: relative;
  .jeopardy-panel-title{
    position: absolute;
    top: -12px;
    right: 18px;
    background: white; /* Matches the card background */
    padding: 0 8px;
    font-size: 12px;
    color: #333;
  }
}
</style>
